/**
 * QChatWindow Component (v5.1 - Mobile Input Fix)
 * Professional chat interface with pure dark theme and crisp typography
 *
 * v5.1 Updates (Mobile Fix):
 * - Removed 70px right padding hack (caused text overlap)
 * - QChatInput now has inline send button (no external padding needed)
 * - Consistent padding on all screen sizes
 *
 * v5.0 Updates (Task 4.5):
 * - Added QConversationHistory dropdown in header
 * - Support for loading past conversations
 *
 * v4.0 Updates (Task 2.3/3.3):
 * - Pass hasConversation prop for continuity indicator
 * - Support for response source display
 *
 * Layout:
 * ┌─────────────────────────────────────┐
 * │  Header (Q + History + New)         │
 * ├─────────────────────────────────────┤
 * │  [Conversation Active Indicator]    │
 * │  Messages Area (scrollable, flex)   │
 * ├─────────────────────────────────────┤
 * │  Input Area (fixed)                 │
 * └─────────────────────────────────────┘
 */

import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Collapse } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QChatMessages from './QChatMessages';
import QChatInput from './QChatInput';
import QConversationHistory from './QConversationHistory';
import QAILogo from './QAILogo';
import { FEATURE_FLAGS } from '../../config/featureFlags';
// QAILoadingProgress moved to QAICoachPage left panel to avoid blocking chat content

/**
 * QChatWindow Component (Enhanced for UI-6, UI-7, UI-8, Task 2.3/3.3, Task 4.5)
 * @param {object} props
 * @param {array} props.messages - Chat messages array
 * @param {number} props.balance - Token balance
 * @param {boolean} props.isLoading - AI is responding
 * @param {boolean} props.isLowBalance - Balance <= 15
 * @param {boolean} props.canSend - Can send messages
 * @param {function} props.onSendMessage - Send message handler
 * @param {function} props.onActionCard - Action card click handler
 * @param {function} props.onActionChip - Action chip click handler
 * @param {function} props.onCancelStream - Cancel streaming handler
 * @param {function} props.onBuyTokens - Buy tokens handler
 * @param {function} props.onClearChat - Clear chat handler
 * @param {string} props.error - Error message if any
 * @param {object} props.progressState - UI-6: Progress state { currentStep, progress, message }
 * @param {array} props.warnings - UI-8: Partial failure warnings
 * @param {boolean} props.hasConversation - Task 2.3: Show conversation continuity indicator
 * @param {string} props.conversationId - Task 4.5: Current conversation ID
 * @param {array} props.conversationHistory - Task 4.5: List of past conversations
 * @param {boolean} props.isLoadingHistory - Task 4.5: Loading conversation history
 * @param {function} props.onSelectConversation - Task 4.5: Load conversation from history
 * @param {function} props.onDeleteConversation - Task 4.5: Delete conversation
 * @param {function} props.onRefreshHistory - Task 4.5: Refresh conversation list
 */
const QChatWindow = ({
  messages = [],
  balance = 0,
  isLoading = false,
  isLowBalance = false,
  canSend = true,
  onSendMessage,
  onActionCard,
  onActionChip,
  onCancelStream,
  onBuyTokens,
  onClearChat,
  error = null,
  progressState = null,
  warnings = [],
  hasConversation = false,  // Task 2.3
  // Task 4.5: Conversation history props
  conversationId = null,
  conversationHistory = [],
  isLoadingHistory = false,
  onSelectConversation,
  onDeleteConversation,
  onRefreshHistory
}) => {
  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [isInputExpanded, setIsInputExpanded] = useState(false);

  // DEBUG: Log input area position on mount and resize
  useEffect(() => {
    const logInputPosition = () => {
      if (inputContainerRef.current) {
        const rect = inputContainerRef.current.getBoundingClientRect();
        console.log('[QChatWindow] Input Area Debug:', {
          top: rect.top,
          bottom: rect.bottom,
          height: rect.height,
          windowHeight: window.innerHeight,
          isVisibleAboveFold: rect.bottom <= window.innerHeight,
          distanceFromBottom: window.innerHeight - rect.bottom
        });
      }
    };

    logInputPosition();
    window.addEventListener('resize', logInputPosition);
    return () => window.removeEventListener('resize', logInputPosition);
  }, []);

  // Handle action card - focus input if needed
  const handleActionCard = useCallback((actionId) => {
    const result = onActionCard?.(actionId);
    if (result?.focusInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [onActionCard]);

  // Handle send
  const handleSend = useCallback((message) => {
    onSendMessage?.(message, 'chat');
    setIsInputExpanded(false);
  }, [onSendMessage]);

  // Toggle input expansion
  const toggleInputExpand = useCallback(() => {
    setIsInputExpanded(prev => !prev);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        backgroundColor: '#0A0A0F',
        borderRadius: '0', // No border radius - clean Bolt style
        overflow: 'hidden',
        border: 'none' // No border - cleaner
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER - T6: Compact 40px Header (UI Density Enhancement)
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: { xs: '4px 8px', sm: '6px 12px' },  // T6: Reduced padding
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          backgroundColor: '#0A0A0F',
          minHeight: '40px',  // T6: Fixed 40px compact header
          maxHeight: '40px'   // T6: Prevent expansion
        }}
      >
        {/* Left: Q.ai Logo + Tagline - Proportional sizing (v4.0) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 } }}>
          {/* Responsive Logo: xs on mobile, sm on desktop */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <QAILogo size="xs" showText />
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
            <QAILogo size="sm" showText />
            {/* Tagline - desktop only */}
            <Typography
              sx={{
                color: '#71717A',
                fontSize: '0.875rem',
                fontWeight: 500,
                marginLeft: '6px',
                display: { xs: 'none', md: 'inline' }
              }}
            >
              AI-Powered Analytic System
            </Typography>
          </Box>
        </Box>

        {/* Right: History + New Chat Only (Token moved to top header) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {/* Task 4.5: Conversation History Dropdown */}
          {onSelectConversation && (
            <QConversationHistory
              conversations={conversationHistory}
              isLoading={isLoadingHistory}
              currentConversationId={conversationId}
              onSelectConversation={onSelectConversation}
              onDeleteConversation={onDeleteConversation}
              onRefresh={onRefreshHistory}
            />
          )}

          {/* New Chat Button - Compact */}
          {onClearChat && (
            <Tooltip title="New chat" arrow placement="bottom">
              <IconButton
                onClick={onClearChat}
                disabled={isLoading}
                size="small"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: '6px',
                  padding: '5px 12px',
                  backgroundColor: 'rgba(39, 181, 109, 0.08)',
                  border: '1px solid rgba(39, 181, 109, 0.15)',
                  color: '#27B56D',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(39, 181, 109, 0.15)',
                    borderColor: 'rgba(39, 181, 109, 0.25)'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#3F3F46'
                  }
                }}
              >
                <AddIcon sx={{ fontSize: '1.125rem' }} />
                <Typography
                  sx={{
                    fontSize: '0.9375rem', // MOBILE FIX: 15px - readable
                    fontWeight: 600
                  }}
                >
                  New
                </Typography>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          MESSAGES AREA - Takes all available space
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <QChatMessages
          messages={messages}
          onActionCard={handleActionCard}
          onActionChip={onActionChip}
          isLoading={isLoading}
          hasConversation={hasConversation}  // Task 2.3
        />
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          ERROR DISPLAY - Minimal Bolt-style
          ════════════════════════════════════════════════════════════════════════ */}
      <Collapse in={!!error}>
        <Box
          sx={{
            padding: '6px 12px',  // PHASE 1.2: Using space-4 (12px) instead of 16px
            backgroundColor: 'rgba(239, 68, 68, 0.06)',
            borderTop: '1px solid rgba(239, 68, 68, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75
          }}
        >
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              backgroundColor: '#EF4444'
            }}
          />
          <Typography
            sx={{
              color: '#EF4444',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          >
            {error}
          </Typography>
        </Box>
      </Collapse>

      {/* ════════════════════════════════════════════════════════════════════════
          INPUT AREA - Compact Bolt-style
          (Loading progress moved to QSignalBoard left panel - no longer blocks chat)
          MOBILE FIX: Ensure input is always visible above any overlays
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        ref={inputContainerRef}
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.04)',
          backgroundColor: '#0A0A0F',
          // Ensure input area stays above FAB and other overlays on mobile
          position: 'relative',
          zIndex: 10,
          // CRITICAL: Prevent input area from shrinking on mobile
          flexShrink: 0,
          // Add safe area padding for devices with home indicators (iOS)
          paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', sm: 0 }
        }}
      >
        {/* Input Component - v5.1: Consistent padding (inline button eliminates FAB overlap) */}
        <Box sx={{
          px: { xs: '12px', sm: '16px' },
          py: { xs: '12px', sm: '10px' }
        }}>
          <QChatInput
            ref={inputRef}
            onSend={handleSend}
            onCancel={onCancelStream}
            isLoading={isLoading}
            disabled={!canSend}
            expanded={isInputExpanded}
            placeholder={
              balance === null
                ? 'Loading...'
                : balance === 0
                ? 'Purchase tokens to continue...'
                : isLowBalance
                ? 'Low balance — add tokens'
                : FEATURE_FLAGS.EXPANDABLE_INPUT
                ? 'Ask Q...'
                : 'Message Q...'
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default QChatWindow;
